/**
 * Collection Filters
 * Handles AJAX-based filtering on collection pages.
 * Fetches the filtered page HTML and swaps the sidebar, active tags, and product grid
 * without a full page reload.
 */

class CollectionFilters {
  constructor(section) {
    this.section = section;
    this.isLoading = false;

    this.init();
  }

  init() {
    this.bindToggle();
    this.bindMobileTrigger();
    this.bindFilterLinks();
    this.bindPriceFilter();
    window.addEventListener('popstate', () => this.fetchAndReplace(window.location.href, false));
  }

  bindMobileTrigger() {
    this.section.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-category-filter-trigger]');
      if (!trigger) return;

      const filter = trigger.closest('[data-category-filter]');
      if (!filter) return;

      const isOpen = filter.classList.contains('category-filter--open');
      filter.classList.toggle('category-filter--open');
      trigger.setAttribute('aria-expanded', !isOpen);
    });
  }

  bindToggle() {
    this.section.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-filter-toggle]');
      if (!toggle) return;

      const group = toggle.closest('.category-filter__group');
      if (!group) return;

      const isOpen = group.classList.contains('category-filter__group--open');
      group.classList.toggle('category-filter__group--open');
      toggle.setAttribute('aria-expanded', !isOpen);
    });
  }

  bindFilterLinks() {
    this.section.addEventListener('click', (e) => {
      const link = e.target.closest('[data-filter-link]');
      if (!link) return;

      e.preventDefault();
      const url = link.getAttribute('href');
      if (url) this.fetchAndReplace(url, true);
    });
  }

  bindPriceFilter() {
    const priceFilter = this.section.querySelector('[data-price-filter]');
    if (!priceFilter) return;

    const applyBtn = priceFilter.querySelector('[data-filter-apply]');
    const priceMin = priceFilter.querySelector('[data-filter-price-min]');
    const priceMax = priceFilter.querySelector('[data-filter-price-max]');

    const applyPrice = () => {
      const url = new URL(window.location.href);

      // Remove existing price params and pagination
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith('filter.v.price')) url.searchParams.delete(key);
      });
      url.searchParams.delete('page');

      if (priceMin && priceMin.value) url.searchParams.set(priceMin.name, priceMin.value);
      if (priceMax && priceMax.value) url.searchParams.set(priceMax.name, priceMax.value);

      this.fetchAndReplace(url.toString(), true);
    };

    if (applyBtn) applyBtn.addEventListener('click', applyPrice);

    [priceMin, priceMax].forEach((input) => {
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyPrice();
          }
        });
      }
    });
  }

  async fetchAndReplace(url, pushState) {
    if (this.isLoading) return;
    this.isLoading = true;
    this.section.classList.add('collection-filters--loading');

    try {
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-collection-filters]');

      if (newSection) {
        // Replace sidebar element (filters + counts update)
        const newSidebar = newSection.querySelector('[data-filter-sidebar]');
        const currentSidebar = this.section.querySelector('[data-filter-sidebar]');
        if (newSidebar && currentSidebar) {
          currentSidebar.replaceWith(newSidebar);
        }

        // Replace product results
        const newResults = newSection.querySelector('[data-filter-results]');
        const currentResults = this.section.querySelector('[data-filter-results]');
        if (newResults && currentResults) {
          currentResults.replaceWith(newResults);
        }
      }

      if (pushState) {
        history.pushState(null, '', url);
      }

      // Re-bind price filter since sidebar DOM was replaced
      this.bindPriceFilter();

      // Notify listeners (e.g. sliders) that collection content was replaced
      document.dispatchEvent(new CustomEvent('collection:filtered', { detail: { section: this.section } }));
    } catch (error) {
      // Fallback to full navigation on error
      window.location.href = url;
    } finally {
      this.isLoading = false;
      this.section.classList.remove('collection-filters--loading');
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('[data-collection-filters]');
  if (section) {
    new CollectionFilters(section);
  }
});

// Re-initialize on Shopify section events (theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-collection-filters]');
    if (section) {
      new CollectionFilters(section);
    }
  });
}
